// ═══════════════════════════════════════════════════════════════════════════════
// DIGITAL TWIN DASHBOARD — Interactive Scenario Simulation
// EnergiPro Solutions — Smart RFQ System v17.0
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useMemo } from 'react';
import { Layout } from '../../components/layout/Layout';
import {
  Shield, AlertTriangle, CheckCircle2, XCircle, ArrowLeft,
  Gauge, Activity, Layers, Eye, ToggleLeft, ToggleRight,
  Minus, Plus, RefreshCw, Cpu, Database, Zap, Box
} from 'lucide-react';
import { MOCK_RFQS, PortalRFQ, PRIORITY_CONFIG, STATUS_CONFIG } from '../portal/portalTypes';
import {
  buildScenario, runSimulation, getRiskColor, getCategoryIcon,
  getCategoryLabel, RiskLevel, EquipmentItem, SimulationResult,
  ScenarioInputs, EquipmentCategory
} from '../../components/rfq/digitalTwinEngine';

// ─── CONFIDENCE GAUGE ────────────────────────────────────────────────────────

const ConfidenceGauge: React.FC<{ score: number }> = ({ score }) => {
  const getColor = () => {
    if (score >= 85) return { ring: 'text-green-400', bg: 'bg-green-500/10', label: 'High Confidence' };
    if (score >= 60) return { ring: 'text-amber-400', bg: 'bg-amber-500/10', label: 'Moderate Confidence' };
    if (score >= 40) return { ring: 'text-orange-400', bg: 'bg-orange-500/10', label: 'Low Confidence' };
    return { ring: 'text-red-400', bg: 'bg-red-500/10', label: 'Critical — Non-Compliant' };
  };
  const { ring, bg, label } = getColor();
  const circumference = 2 * Math.PI * 40;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  return (
    <div className={`${bg} border border-slate-700/50 rounded-xl p-6 flex flex-col items-center`}>
      <div className="relative w-28 h-28">
        <svg className="w-28 h-28 transform -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="40" stroke="currentColor" strokeWidth="8" fill="none" className="text-slate-700/50" />
          <circle cx="50" cy="50" r="40" stroke="currentColor" strokeWidth="8" fill="none"
            className={ring}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 0.8s ease' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`text-2xl font-bold font-['Oswald'] ${ring}`}>{score}</span>
          <span className="text-slate-500 text-[9px]">/100</span>
        </div>
      </div>
      <p className={`mt-3 text-xs font-medium ${ring}`}>{label}</p>
      <p className="text-slate-500 text-[9px] mt-0.5">Operational Confidence</p>
    </div>
  );
};

// ─── CONFIDENCE BREAKDOWN ────────────────────────────────────────────────────

const ConfidenceBreakdownPanel: React.FC<{ breakdown: SimulationResult['confidenceBreakdown'] }> = ({ breakdown }) => {
  const items = [
    { label: 'Pressure', value: breakdown.pressureCompliance, max: 25 },
    { label: 'Safety', value: breakdown.safetyCompliance, max: 25 },
    { label: 'Capacity', value: breakdown.capacityCompliance, max: 25 },
    { label: 'Environment', value: breakdown.environmentalCompliance, max: 25 },
  ];

  return (
    <div className="space-y-3">
      {items.map(item => {
        const pct = (item.value / item.max) * 100;
        const color = pct >= 80 ? 'bg-green-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500';
        return (
          <div key={item.label}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-slate-400 text-xs">{item.label}</span>
              <span className="text-white text-xs font-bold">{item.value}/{item.max}</span>
            </div>
            <div className="h-2 bg-slate-700/50 rounded-full overflow-hidden">
              <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── RISK BADGE ──────────────────────────────────────────────────────────────

const RiskBadge: React.FC<{ level: RiskLevel; large?: boolean }> = ({ level, large }) => {
  const colors = getRiskColor(level);
  return (
    <span className={`inline-flex items-center gap-1.5 px-${large ? '4' : '2.5'} py-${large ? '2' : '1'} rounded-${large ? 'xl' : 'full'} ${colors.bg} border ${colors.border} ${colors.text} text-${large ? 'sm' : '[10px]'} font-bold uppercase`}>
      {level === 'critical' && <XCircle className={`w-${large ? '4' : '3'} h-${large ? '4' : '3'}`} />}
      {level === 'high' && <AlertTriangle className={`w-${large ? '4' : '3'} h-${large ? '4' : '3'}`} />}
      {level === 'medium' && <Gauge className={`w-${large ? '4' : '3'} h-${large ? '4' : '3'}`} />}
      {level === 'low' && <CheckCircle2 className={`w-${large ? '4' : '3'} h-${large ? '4' : '3'}`} />}
      {level}
    </span>
  );
};

// ─── EQUIPMENT CARD ──────────────────────────────────────────────────────────

const EquipmentCard: React.FC<{
  item: EquipmentItem;
  removed: boolean;
  onToggle: () => void;
}> = ({ item, removed, onToggle }) => {
  const riskColors = getRiskColor(item.riskIfRemoved);

  return (
    <div className={`border rounded-xl p-4 transition-all ${removed ? 'bg-red-500/5 border-red-500/30 opacity-60' : 'bg-slate-800/30 border-slate-700/50'}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-2.5">
          <span className="text-lg">{getCategoryIcon(item.category)}</span>
          <div>
            <p className={`text-sm font-medium ${removed ? 'text-slate-500 line-through' : 'text-white'}`}>{item.name}</p>
            <p className="text-slate-500 text-[10px] mt-0.5">{item.rating}</p>
          </div>
        </div>
        <button
          onClick={onToggle}
          className={`p-1.5 rounded-lg border transition-all ${removed ? 'bg-green-500/10 border-green-500/30 text-green-400 hover:bg-green-500/20' : 'bg-red-500/10 border-red-500/30 text-red-400 hover:bg-red-500/20'}`}
          title={removed ? 'Restore equipment' : 'Remove (what-if)'}
        >
          {removed ? <Plus className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
        </button>
      </div>
      <p className="text-slate-400 text-[10px] mt-2">{item.reason}</p>
      <div className="flex items-center gap-2 mt-2">
        {item.required && <span className="text-orange-300 text-[9px] font-bold uppercase bg-orange-500/10 px-2 py-0.5 rounded border border-orange-500/30">Mandatory</span>}
        <span className={`text-[9px] ${riskColors.text}`}>Risk if removed: {item.riskIfRemoved}</span>
      </div>
    </div>
  );
};

// ─── WHAT-IF SLIDERS ─────────────────────────────────────────────────────────

const WhatIfControls: React.FC<{
  inputs: ScenarioInputs;
  onChange: (inputs: ScenarioInputs) => void;
}> = ({ inputs, onChange }) => {
  return (
    <div className="bg-[#111827] border border-purple-500/20 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <RefreshCw className="w-4 h-4 text-purple-400" />
        <h3 className="font-['Oswald'] text-sm text-white uppercase tracking-wide">What-If Analysis</h3>
      </div>
      <p className="text-slate-500 text-[10px] mb-4">Adjust parameters to simulate different scenarios and observe impact on risk and confidence.</p>

      <div className="space-y-5">
        {/* Pressure Slider */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-slate-400 text-xs">Wellhead Pressure</label>
            <span className="text-white text-xs font-bold font-mono">{inputs.pressure.toLocaleString()} PSI</span>
          </div>
          <input
            type="range" min="0" max="15000" step="500"
            value={inputs.pressure}
            onChange={e => onChange({ ...inputs, pressure: parseInt(e.target.value) })}
            className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-orange-500"
          />
          <div className="flex justify-between text-slate-600 text-[9px] mt-0.5">
            <span>0</span><span>5K</span><span>10K</span><span>15K</span>
          </div>
        </div>

        {/* Oil Rate Slider */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-slate-400 text-xs">Oil Rate</label>
            <span className="text-white text-xs font-bold font-mono">{inputs.oilRate.toLocaleString()} BOPD</span>
          </div>
          <input
            type="range" min="0" max="20000" step="1000"
            value={inputs.oilRate}
            onChange={e => onChange({ ...inputs, oilRate: parseInt(e.target.value) })}
            className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-orange-500"
          />
          <div className="flex justify-between text-slate-600 text-[9px] mt-0.5">
            <span>0</span><span>5K</span><span>10K</span><span>15K</span><span>20K</span>
          </div>
        </div>

        {/* Gas Rate Slider */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-slate-400 text-xs">Gas Rate</label>
            <span className="text-white text-xs font-bold font-mono">{inputs.gasRate} MMSCFD</span>
          </div>
          <input
            type="range" min="0" max="60" step="5"
            value={inputs.gasRate}
            onChange={e => onChange({ ...inputs, gasRate: parseInt(e.target.value) })}
            className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-orange-500"
          />
          <div className="flex justify-between text-slate-600 text-[9px] mt-0.5">
            <span>0</span><span>15</span><span>30</span><span>45</span><span>60</span>
          </div>
        </div>

        {/* H2S Toggle */}
        <div className="flex items-center justify-between py-2 border-t border-b border-slate-700/30">
          <div>
            <p className="text-slate-300 text-xs">H₂S Presence</p>
            <p className="text-slate-600 text-[9px]">Toggle sour gas scenario</p>
          </div>
          <button
            onClick={() => onChange({ ...inputs, h2sPresence: !inputs.h2sPresence })}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
              inputs.h2sPresence
                ? 'bg-red-500/10 border-red-500/40 text-red-300'
                : 'bg-slate-800 border-slate-600 text-slate-400'
            }`}
          >
            {inputs.h2sPresence ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
            {inputs.h2sPresence ? 'Present' : 'Absent'}
          </button>
        </div>

        {/* Sand Production */}
        <div>
          <p className="text-slate-400 text-xs mb-2">Sand Production</p>
          <div className="flex gap-2">
            {(['none', 'low', 'medium', 'high'] as const).map(level => (
              <button
                key={level}
                onClick={() => onChange({ ...inputs, sandProduction: level })}
                className={`flex-1 py-2 rounded-lg border text-[10px] font-bold uppercase transition-all ${
                  inputs.sandProduction === level
                    ? 'bg-orange-500/10 border-orange-500/40 text-orange-300'
                    : 'bg-slate-800 border-slate-700 text-slate-500 hover:text-slate-300'
                }`}
              >
                {level}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── MAIN DIGITAL TWIN PAGE ──────────────────────────────────────────────────

export const DigitalTwinPage: React.FC = () => {
  const [selectedRFQ, setSelectedRFQ] = useState<PortalRFQ | null>(null);
  const [removedEquipment, setRemovedEquipment] = useState<string[]>([]);
  const [whatIfInputs, setWhatIfInputs] = useState<ScenarioInputs | null>(null);

  const activeRFQs = MOCK_RFQS.filter(r => r.status !== 'closed');

  // Run simulation
  const simulation = useMemo(() => {
    if (!selectedRFQ) return null;
    const scenario = buildScenario(selectedRFQ);

    // If what-if mode active, use modified inputs
    if (whatIfInputs) {
      const modifiedScenario = { ...scenario, inputs: whatIfInputs };
      return runSimulation(modifiedScenario, removedEquipment);
    }
    return runSimulation(scenario, removedEquipment);
  }, [selectedRFQ, removedEquipment, whatIfInputs]);

  const toggleEquipment = (id: string) => {
    setRemovedEquipment(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const resetSimulation = () => {
    setRemovedEquipment([]);
    if (selectedRFQ) {
      const scenario = buildScenario(selectedRFQ);
      setWhatIfInputs(scenario.inputs);
    }
  };

  // RFQ Selection Screen
  if (!selectedRFQ) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#0a0f1e] py-12">
          <div className="max-w-5xl mx-auto px-4">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-purple-500/10 rounded-lg border border-purple-500/30">
                <Cpu className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h1 className="font-['Oswald'] text-3xl text-white uppercase tracking-wide">Digital Twin</h1>
                <p className="text-slate-400 text-sm">Scenario Simulation & Risk Analysis</p>
              </div>
            </div>

            <p className="text-slate-400 text-sm mb-6">Select an RFQ to generate a digital twin scenario and run simulations.</p>

            <div className="grid gap-3">
              {activeRFQs.map(rfq => (
                <button
                  key={rfq.id}
                  onClick={() => {
                    setSelectedRFQ(rfq);
                    const scenario = buildScenario(rfq);
                    setWhatIfInputs(scenario.inputs);
                    setRemovedEquipment([]);
                  }}
                  className="bg-[#111827] border border-slate-700/50 rounded-xl p-5 text-left hover:border-purple-500/40 transition-all group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Database className="w-4 h-4 text-purple-400 group-hover:text-purple-300 transition-colors" />
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-orange-300 font-mono text-xs">{rfq.id}</span>
                          <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase border ${PRIORITY_CONFIG[rfq.priority].bgColor} ${PRIORITY_CONFIG[rfq.priority].borderColor} ${PRIORITY_CONFIG[rfq.priority].color}`}>{rfq.priority}</span>
                        </div>
                        <p className="text-white text-sm font-medium mt-0.5">{rfq.serviceLabel}</p>
                        <p className="text-slate-500 text-xs">{rfq.client} — {rfq.company} — {rfq.countryLabel}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-white text-xs font-mono">{rfq.wellheadPressure || '—'} PSI</p>
                      <p className="text-slate-500 text-[10px]">{rfq.h2sPresence === 'yes' ? '⚠ H₂S' : 'Sweet'} • Sand: {rfq.sandProduction || 'None'}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!simulation) return null;

  // Group equipment by category
  const equipmentByCategory = simulation.equipment.reduce<Record<string, EquipmentItem[]>>((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  const triggeredRisks = simulation.risks.filter(r => r.triggered);

  return (
    <Layout>
      <div className="min-h-screen bg-[#0a0f1e] py-12">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header */}
          <button onClick={() => { setSelectedRFQ(null); setRemovedEquipment([]); }} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm transition-colors mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to RFQ Selection
          </button>

          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/10 rounded-lg border border-purple-500/30">
                <Cpu className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h1 className="font-['Oswald'] text-2xl text-white uppercase tracking-wide">Digital Twin — {selectedRFQ.serviceLabel}</h1>
                <p className="text-slate-400 text-xs mt-0.5">
                  Scenario: <span className="text-purple-300 font-mono">{simulation.scenario.id}</span> •
                  RFQ: <span className="text-orange-300 font-mono">{selectedRFQ.id}</span> •
                  {selectedRFQ.client} — {selectedRFQ.company}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <RiskBadge level={simulation.overallRisk} large />
              <button onClick={resetSimulation} className="flex items-center gap-1.5 px-3 py-2 bg-slate-800 border border-slate-600 text-slate-300 rounded-lg text-xs hover:bg-slate-700 transition-all">
                <RefreshCw className="w-3.5 h-3.5" /> Reset
              </button>
            </div>
          </div>

          {/* Warnings Banner */}
          {simulation.warnings.length > 0 && (
            <div className="bg-red-500/5 border border-red-500/30 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <span className="text-red-300 text-xs font-bold uppercase">Configuration Warnings ({simulation.warnings.length})</span>
              </div>
              <div className="space-y-1">
                {simulation.warnings.map((w, i) => (
                  <p key={i} className="text-red-200/80 text-xs">{w}</p>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* LEFT: What-If Controls */}
            <div className="space-y-5">
              {whatIfInputs && (
                <WhatIfControls inputs={whatIfInputs} onChange={setWhatIfInputs} />
              )}

              {/* Confidence Gauge */}
              <ConfidenceGauge score={simulation.confidenceScore} />

              {/* Confidence Breakdown */}
              <div className="bg-[#111827] border border-slate-700/50 rounded-xl p-5">
                <h3 className="font-['Oswald'] text-xs text-slate-400 uppercase tracking-wide mb-4">Compliance Breakdown</h3>
                <ConfidenceBreakdownPanel breakdown={simulation.confidenceBreakdown} />
              </div>
            </div>

            {/* CENTER: Equipment Configuration */}
            <div className="space-y-5">
              <div className="bg-[#111827] border border-slate-700/50 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Box className="w-4 h-4 text-orange-400" />
                    <h3 className="font-['Oswald'] text-sm text-white uppercase tracking-wide">Recommended Configuration</h3>
                  </div>
                  <span className="text-slate-500 text-[10px]">{simulation.equipment.length} items</span>
                </div>

                <div className="space-y-5">
                  {Object.entries(equipmentByCategory).map(([category, items]) => (
                    <div key={category}>
                      <div className="flex items-center gap-2 mb-2">
                        <span>{getCategoryIcon(category as EquipmentCategory)}</span>
                        <span className="text-slate-400 text-xs font-bold uppercase">{getCategoryLabel(category as EquipmentCategory)}</span>
                        <span className="text-slate-600 text-[9px]">({items.length})</span>
                      </div>
                      <div className="space-y-2">
                        {items.map(item => (
                          <EquipmentCard
                            key={item.id}
                            item={item}
                            removed={removedEquipment.includes(item.id)}
                            onToggle={() => toggleEquipment(item.id)}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* RIGHT: Risk Profile */}
            <div className="space-y-5">
              {/* Overall Risk */}
              <div className={`border rounded-xl p-5 ${getRiskColor(simulation.overallRisk).bg} ${getRiskColor(simulation.overallRisk).border}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="w-4 h-4 text-orange-400" />
                  <h3 className="font-['Oswald'] text-sm text-white uppercase tracking-wide">Risk Profile</h3>
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <RiskBadge level={simulation.overallRisk} large />
                  <span className="text-slate-400 text-xs">
                    {simulation.compliant ? '✓ All mandatory equipment present' : '✗ Non-compliant configuration'}
                  </span>
                </div>
              </div>

              {/* Risk Factors */}
              <div className="bg-[#111827] border border-slate-700/50 rounded-xl p-5">
                <h3 className="font-['Oswald'] text-xs text-slate-400 uppercase tracking-wide mb-4">Risk Factors ({triggeredRisks.length} Active)</h3>
                <div className="space-y-3">
                  {triggeredRisks.map(risk => {
                    const colors = getRiskColor(risk.level);
                    return (
                      <div key={risk.id} className={`${colors.bg} border ${colors.border} rounded-lg p-3`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-white text-xs font-medium">{risk.category}</span>
                          <RiskBadge level={risk.level} />
                        </div>
                        <p className="text-slate-300 text-[10px]">{risk.description}</p>
                        <p className="text-slate-500 text-[9px] mt-1.5 italic">Mitigation: {risk.mitigation}</p>
                      </div>
                    );
                  })}
                  {triggeredRisks.length === 0 && (
                    <p className="text-slate-500 text-sm text-center py-4">No significant risks identified</p>
                  )}
                </div>
              </div>

              {/* Scenario Inputs Summary */}
              <div className="bg-[#111827] border border-slate-700/50 rounded-xl p-5">
                <h3 className="font-['Oswald'] text-xs text-slate-400 uppercase tracking-wide mb-3">Active Scenario Inputs</h3>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div><span className="text-slate-500 text-[10px]">Pressure</span><p className="text-white font-mono">{whatIfInputs?.pressure?.toLocaleString() || '—'} PSI</p></div>
                  <div><span className="text-slate-500 text-[10px]">Oil Rate</span><p className="text-white font-mono">{whatIfInputs?.oilRate?.toLocaleString() || '—'} BOPD</p></div>
                  <div><span className="text-slate-500 text-[10px]">Gas Rate</span><p className="text-white font-mono">{whatIfInputs?.gasRate || '—'} MMSCFD</p></div>
                  <div><span className="text-slate-500 text-[10px]">H₂S</span><p className={whatIfInputs?.h2sPresence ? 'text-red-300 font-bold' : 'text-white'}>{whatIfInputs?.h2sPresence ? '⚠ Present' : 'Absent'}</p></div>
                  <div><span className="text-slate-500 text-[10px]">Sand</span><p className="text-white capitalize">{whatIfInputs?.sandProduction || '—'}</p></div>
                  <div><span className="text-slate-500 text-[10px]">Environment</span><p className="text-white capitalize">{whatIfInputs?.environment || '—'}</p></div>
                </div>
              </div>

              {/* Guardrails */}
              <div className="bg-slate-800/30 border border-slate-700/30 rounded-xl p-4">
                <div className="flex items-start gap-2.5">
                  <Shield className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-slate-400 text-xs font-medium">Digital Twin Disclaimer</p>
                    <p className="text-slate-500 text-[10px] mt-1">This simulation provides decision support only. It does NOT replace engineering judgment, field assessment, or formal risk analysis. All equipment selections must be reviewed and approved by a qualified engineer.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
